import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { Globe } from "Shared/appIcons";
import type { NavItem } from "Shared/Layouts/components/NavButton";
import { ThemeToggle } from "Shared/Layouts/components/ThemeToggle";
import { HorizontalNavButton } from "Shared/Layouts/components/TopbarNav/HorizontalNavButton";
import { UserMenu } from "Shared/Layouts/components/TopbarNav/UserMenu";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";

type Props = {
	navItems: NavItem[];
	isActive: (to?: string) => boolean;
	userLogin: string;
	userInitials: string;
	onSignOut: () => void;
};

/**
 * Horizontal top navigation: brand · nav items · theme/lang · user menu.
 * Hidden below the `md` breakpoint — `DrawerNav` takes over there.
 */
export const TopbarNav = ({
	navItems,
	isActive,
	userLogin,
	userInitials,
	onSignOut,
}: Props) => {
	const { t } = useResource();
	const top = navItems.filter((i) => i.group === "top");
	const bottom = navItems.filter((i) => i.group === "bottom");
	const hasBottom = bottom.length > 0;

	return (
		<header className="border-border bg-card hidden h-14 flex-shrink-0 items-center gap-6 border-b px-6 md:flex">
			<Link
				to={AppRouting.Dashboard.path()}
				className="group flex flex-shrink-0 items-center gap-2.5 no-underline"
				title={t(Resources.Sidebar.BrandTitle)}
			>
				<div className="bg-primary group-hover:bg-primary/90 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition-colors">
					<span className="text-primary-foreground text-[13px] font-bold tracking-tight">
						IL
					</span>
				</div>
				<div className="hidden min-w-0 flex-col leading-tight lg:flex">
					<span className="text-foreground truncate text-[13px] font-semibold">
						{t(Resources.Sidebar.BrandTitle)}
					</span>
					<span className="text-muted-foreground truncate text-[10px]">
						{t(Resources.Sidebar.BrandSubtitle)}
					</span>
				</div>
			</Link>

			<div className="bg-border hidden h-5 w-px lg:block" />

			<nav className="flex min-w-0 flex-shrink items-center gap-1">
				{top.map((item) => (
					<HorizontalNavButton
						key={item.key}
						item={item}
						active={isActive(item.to)}
					/>
				))}
				{hasBottom ? (
					<>
						<div className="bg-border mx-1.5 h-5 w-px" />
						{bottom.map((item) => (
							<HorizontalNavButton
								key={item.key}
								item={item}
								active={isActive(item.to)}
							/>
						))}
					</>
				) : null}
			</nav>

			<div className="flex-1" />

			<div className="flex flex-shrink-0 items-center gap-1">
				<ThemeToggle />
				<Button
					variant="ghost"
					size="icon-sm"
					className="text-muted-foreground hover:text-foreground"
					title={t(Resources.Sidebar.LanguageSwitcher)}
				>
					<Globe className="h-4 w-4" />
				</Button>
				<div className="bg-border mx-1 h-5 w-px" />
				<UserMenu
					userLogin={userLogin}
					userInitials={userInitials}
					onSignOut={onSignOut}
				/>
			</div>
		</header>
	);
};
